import { Chip, ChipGroup } from '@src/components/common/ChipGroup';
import { ConfirmModal } from '@src/components/common/ConfirmModal';
import Spinner from '@src/components/common/Spinner';
import { uploadedMediaCardTypeFor } from '@src/services/videoTemplate/uploadedMediaCard';
import { observer } from 'mobx-react-lite';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import DeleteIcon from '../../../svgs/DeleteIcon';
import { Breadcrumbs } from '../../Breadcrumbs';
import { useCompositionContext } from '../CompositionContext';
import { useVideoTemplatesContext } from '../VideoTemplateSettings/VideoTemplatesContext';
import { CompositionControls } from './CompositionControls';
import {
  ARBOR_TEMPLATES_LABEL,
  resolveTemplateGroup,
  templateGroupLabel,
  WORKSPACE_TEMPLATES_LABEL,
} from './templateGroup';
import { UploadMediaCardTile } from './UploadMediaCardTile';

import type { VideoTemplateConfigured } from '../VideoTemplateSettings/types';
import type { CompositionTemplate } from './types';
import type { ElementState } from '@src/renderer/ElementState';

interface CompositionSettingsProps {
  activeElement: ElementState | null | undefined;
  onAddComposition: (composition: CompositionTemplate) => void;
  isQuoteCard?: boolean;
  activeTemplateId?: string;
  hideGlobalPresets?: boolean;
}

export const CompositionSettings = observer(
  (props: CompositionSettingsProps) => {
    const [deleteCompositionId, setDeleteCompositionId] = useState<
      string | null
    >(null);
    const {
      activeElement,
      onAddComposition,
      isQuoteCard,
      activeTemplateId,
      hideGlobalPresets,
    } = props;
    // Get composition data from context
    let activeTemplate: VideoTemplateConfigured | undefined;
    let replacedElements: { [key: string]: string } = {};
    try {
      const videoTemplateContext = useVideoTemplatesContext();
      activeTemplate = videoTemplateContext.activeTemplate;
      replacedElements =
        videoTemplateContext.activeTemplateSettings?.replacedElementIds || {};
      // eslint-disable-next-line no-empty
    } catch (error) {
      console.warn('[CompositionSettings]: ' + error);
    }

    const activeNamecardId = useMemo(() => {
      const replacedNamecardId = replacedElements?.nameCard;
      return (
        replacedNamecardId ||
        activeTemplate?.configuration?.namecardConfiguration
          ?.compositionTemplate?.id
      );
    }, [activeTemplate]);

    const {
      setSelectedSubcategoryId,
      deleteComposition,
      selectedCategory,
      selectedSubcategory,
      compositions,
      categories,
      pendingThumbnailIds,
    } = useCompositionContext();
    const [activeSubcategoryChipId, setActiveSubcategoryChipId] = useState<
      string | null
    >(null);
    const [activeTemplateChip, setActiveTemplateChip] = useState<
      'my' | 'arbor'
    >('my');

    const deleteCompositionTemplate = async (compositionId: string) => {
      setDeleteCompositionId(null);
      deleteComposition(compositionId);
    };

    const renderCompositionSettings = () => {
      if (!activeElement) return null;
      return <CompositionControls activeElement={activeElement} />;
    };

    // Helper function to render a single composition card
    const renderCompositionCard = useCallback(
      (composition: CompositionTemplate) => {
        const isActive =
          activeTemplateId === composition.id ||
          activeNamecardId === composition.id;

        return (
          <CompositionCard
            key={composition.id}
            composition={composition}
            isActive={isActive}
            isQuoteCard={isQuoteCard}
            isThumbnailPending={pendingThumbnailIds.has(composition.id)}
            onAdd={onAddComposition}
            onDelete={
              !composition.isGlobalPreset
                ? () => setDeleteCompositionId(composition.id)
                : undefined
            }
          />
        );
      },
      [
        onAddComposition,
        activeNamecardId,
        activeTemplateId,
        isQuoteCard,
        pendingThumbnailIds,
      ],
    );

    // PLAT-11732 — an intro / outro grid leads with an upload tile: the
    // creator's own image or video, added as a full-frame card
    const renderUploadTile = useCallback(
      (designs: CompositionTemplate[], subcategoryId: string | undefined) => {
        const cardType = uploadedMediaCardTypeFor(designs);
        return cardType ? (
          <UploadMediaCardTile
            cardType={cardType}
            target={
              selectedCategory && subcategoryId
                ? { categoryId: selectedCategory.id, subcategoryId }
                : undefined
            }
            onAdd={onAddComposition}
          />
        ) : null;
      },
      [onAddComposition, selectedCategory],
    );

    // Render my/arbor template chips + grid for a given composition list
    const renderTemplateChipsAndGrid = useCallback(
      (allCompositions: CompositionTemplate[], subcategoryId?: string) => {
        const [arborCompositions, myCompositions] = allCompositions.reduce<
          [CompositionTemplate[], CompositionTemplate[]]
        >(
          ([arbor, mine], composition) => {
            if (composition.isGlobalPreset) {
              arbor.push(composition);
            } else {
              mine.push(composition);
            }
            return [arbor, mine];
          },
          [[], []],
        );

        if (activeNamecardId) {
          arborCompositions.sort((a, b) => {
            if (a.id === activeNamecardId) return -1;
            if (b.id === activeNamecardId) return 1;
            return 0;
          });
          myCompositions.sort((a, b) => {
            if (a.id === activeNamecardId) return -1;
            if (b.id === activeNamecardId) return 1;
            return 0;
          });
        }

        const { effectiveChip, hasMy, hasArbor } = resolveTemplateGroup(
          allCompositions,
          activeTemplateChip,
          hideGlobalPresets,
        );
        const showChips = hasMy && hasArbor;
        const visibleCompositions =
          effectiveChip === 'my' ? myCompositions : arborCompositions;

        return (
          <>
            {showChips && (
              <SubcategoryChips>
                <SubcategoryChip
                  isSelected={effectiveChip === 'my'}
                  onClick={() => setActiveTemplateChip('my')}
                >
                  {WORKSPACE_TEMPLATES_LABEL}
                </SubcategoryChip>
                <SubcategoryChip
                  isSelected={effectiveChip === 'arbor'}
                  onClick={() => setActiveTemplateChip('arbor')}
                >
                  {ARBOR_TEMPLATES_LABEL}
                </SubcategoryChip>
              </SubcategoryChips>
            )}
            <CompositionGridWrapper>
              {renderUploadTile(allCompositions, subcategoryId)}
              {visibleCompositions.map(renderCompositionCard)}
            </CompositionGridWrapper>
          </>
        );
      },
      [
        renderCompositionCard,
        renderUploadTile,
        activeNamecardId,
        hideGlobalPresets,
        activeTemplateChip,
      ],
    );

    // A single-subcategory category renders the subcategory grid directly, so
    // resolve its lone subcategory at render time. Waiting for the auto-select
    // effect below leaves the body empty for a frame — and when the category
    // reference is unchanged (re-clicking the already-active category tab only
    // resets the subcategory) the effect never re-fires, so the panel body
    // stayed permanently empty and the floating panel collapsed (PLAT-11401).
    const effectiveSubcategory =
      selectedSubcategory ??
      (selectedCategory?.subcategories.length === 1
        ? selectedCategory.subcategories[0]
        : undefined);

    // Render all compositions for a specific subcategory (used in "See All" mode)
    const renderAllCompositionsForSubcategory = useCallback(
      (disableBreadcrumbs: boolean = false) => {
        if (!selectedCategory || !effectiveSubcategory) return null;

        const { effectiveChip } = resolveTemplateGroup(
          effectiveSubcategory.compositions,
          activeTemplateChip,
          hideGlobalPresets,
        );

        return (
          <AllCompositionsForSubcategoryWrapper>
            {!disableBreadcrumbs && (
              <SubcategoryHeader>
                <Breadcrumbs
                  items={[
                    {
                      label: selectedCategory?.label || '',
                      onClick: () => {
                        setSelectedSubcategoryId(null);
                      },
                    },
                    {
                      // Segment 2 reflects the My/Arbor group the user is
                      // browsing, not the (often duplicate) subcategory name.
                      label: templateGroupLabel(effectiveChip === 'arbor'),
                    },
                  ]}
                />
              </SubcategoryHeader>
            )}
            {renderTemplateChipsAndGrid(
              effectiveSubcategory.compositions,
              effectiveSubcategory.id,
            )}
          </AllCompositionsForSubcategoryWrapper>
        );
      },
      [
        selectedCategory,
        effectiveSubcategory,
        setSelectedSubcategoryId,
        renderTemplateChipsAndGrid,
        activeTemplateChip,
        hideGlobalPresets,
      ],
    );

    // Render the category tabs and subcategory carousels
    const renderCategoryView = useCallback(() => {
      if (!selectedCategory) return null;

      const visibleSubcategories = selectedCategory.subcategories.filter(
        sub => {
          const visible = hideGlobalPresets
            ? sub.compositions.filter(c => !c.isGlobalPreset)
            : sub.compositions;
          return visible.length > 0;
        },
      );

      const activeSubcategory = visibleSubcategories.find(
        sub => sub.id === activeSubcategoryChipId,
      );
      const activeCompositions = activeSubcategory
        ? hideGlobalPresets
          ? activeSubcategory.compositions.filter(c => !c.isGlobalPreset)
          : [...activeSubcategory.compositions]
        : [];

      const hasActiveNamecard = activeCompositions.some(
        c => c.id === activeNamecardId,
      );
      if (hasActiveNamecard) {
        activeCompositions.sort((a, b) => {
          if (a.id === activeNamecardId) return -1;
          if (b.id === activeNamecardId) return 1;
          return 0;
        });
      }

      return (
        <AllCompositionsForSubcategoryWrapper>
          {visibleSubcategories.length > 1 && (
            <SubcategoryChips>
              {visibleSubcategories.map(sub => (
                <SubcategoryChip
                  key={sub.id}
                  isSelected={sub.id === activeSubcategoryChipId}
                  onClick={() => setActiveSubcategoryChipId(sub.id)}
                >
                  {chipLabel(sub.label, selectedCategory.label)}
                </SubcategoryChip>
              ))}
            </SubcategoryChips>
          )}
          {activeSubcategory && (
            <CompositionGridWrapper>
              {renderUploadTile(activeCompositions, activeSubcategory.id)}
              {activeCompositions.map(renderCompositionCard)}
            </CompositionGridWrapper>
          )}
        </AllCompositionsForSubcategoryWrapper>
      );
    }, [
      selectedCategory,
      activeSubcategoryChipId,
      renderCompositionCard,
      renderUploadTile,
      activeNamecardId,
      hideGlobalPresets,
    ]);

    // Render compositions directly when there are no categories (e.g., Default Quote Card fallback)
    const renderCompositionsWithoutCategories = useCallback(() => {
      if (!compositions || compositions.length === 0) return null;

      return (
        <AllCompositionsForSubcategoryWrapper>
          {renderTemplateChipsAndGrid(compositions)}
        </AllCompositionsForSubcategoryWrapper>
      );
    }, [compositions, renderTemplateChipsAndGrid]);

    useEffect(() => {
      if (selectedCategory?.subcategories.length === 1) {
        setSelectedSubcategoryId(selectedCategory.subcategories[0].id);
      }
    }, [selectedCategory, setSelectedSubcategoryId]);

    // Auto-select first subcategory chip when category changes. Saving a
    // template (e.g. an Outro upload) rebuilds the category object too, so
    // keep the current chip while it's still listed (PLAT-11732).
    useEffect(() => {
      if (selectedCategory?.subcategories?.length) {
        const withCompositions = selectedCategory.subcategories.filter(sub => {
          const visible = hideGlobalPresets
            ? sub.compositions.filter(c => !c.isGlobalPreset)
            : sub.compositions;
          return visible.length > 0;
        });
        setActiveSubcategoryChipId(prev =>
          withCompositions.some(sub => sub.id === prev)
            ? prev
            : (withCompositions[0]?.id ?? null),
        );
      } else {
        setActiveSubcategoryChipId(null);
      }
    }, [selectedCategory, hideGlobalPresets]);

    // Determine if we have categories to show or should fall back to direct composition rendering
    const hasCategories = categories && categories.length > 0;

    return (
      <CompositionSettingsContainer data-testid="composition-settings-panel">
        {/* Fallback: render compositions directly when no categories exist */}
        {!activeElement &&
          !hasCategories &&
          renderCompositionsWithoutCategories()}
        {!activeElement &&
          hasCategories &&
          !effectiveSubcategory &&
          (selectedCategory?.subcategories?.length || 0) > 1 &&
          renderCategoryView()}
        {!activeElement &&
          hasCategories &&
          effectiveSubcategory &&
          renderAllCompositionsForSubcategory(
            selectedCategory?.subcategories?.length === 1,
          )}
        {activeElement && renderCompositionSettings()}
        {deleteCompositionId && (
          <ConfirmModal
            closeModal={() => setDeleteCompositionId(null)}
            onConfirm={() => deleteCompositionTemplate(deleteCompositionId)}
            onCancel={() => setDeleteCompositionId(null)}
            confirmText="Delete"
            cancelText="Cancel"
            title="Delete this composition?"
            message="This cannot be undone."
          />
        )}
      </CompositionSettingsContainer>
    );
  },
);

/**
 * A subcategory chip's label without the category tab's own name: under the
 * Cards tab, "Intro Cards" reads as "Intro" so the chips fit on one line.
 */
function chipLabel(subcategoryLabel: string, categoryLabel: string): string {
  const suffix = ` ${categoryLabel}`;
  return categoryLabel &&
    subcategoryLabel.length > suffix.length &&
    subcategoryLabel.toLowerCase().endsWith(suffix.toLowerCase())
    ? subcategoryLabel.slice(0, -suffix.length)
    : subcategoryLabel;
}

/** Individual composition card with Apply hover button and optional delete. */
function CompositionCard({
  composition,
  isActive,
  isQuoteCard,
  isThumbnailPending,
  onAdd,
  onDelete,
}: {
  composition: CompositionTemplate;
  isActive: boolean;
  isQuoteCard?: boolean;
  /** A just-saved template whose rendered thumbnail hasn't landed yet. */
  isThumbnailPending?: boolean;
  onAdd: (c: CompositionTemplate) => void;
  onDelete?: () => void;
}) {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <CompositionCardWrapper>
      <CardBorder
        isActive={isActive}
        isHovered={isHovered}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={() => onAdd(composition)}
      >
        <AddCompositionButton isQuoteCard={isQuoteCard}>
          {/* While pending, a stand-in thumbnail (an uploaded card's video
              file) may not render as an image, so the tile says why it's
              empty rather than showing the bare label. */}
          {isThumbnailPending ? (
            <PendingThumbnail isQuoteCard={isQuoteCard}>
              <Spinner size="sm" />
              <PendingThumbnailText>Generating…</PendingThumbnailText>
            </PendingThumbnail>
          ) : composition.thumbnail?.url ? (
            <ThumbnailImage
              isQuoteCard={isQuoteCard}
              src={composition.thumbnail.url}
              alt={composition.label}
              loading="lazy"
            />
          ) : (
            <PlaceholderContent isQuoteCard={isQuoteCard}>
              {composition.label}
            </PlaceholderContent>
          )}
          {isHovered && (
            <>
              <PresetDimOverlay />
              <GlassButtonWrapper>
                <GlassButton>Apply</GlassButton>
              </GlassButtonWrapper>
            </>
          )}
        </AddCompositionButton>
      </CardBorder>
      {onDelete && (
        <DeleteButton
          onClick={e => {
            e.stopPropagation();
            onDelete();
          }}
        >
          <DeleteIcon />
        </DeleteButton>
      )}
      <CompositionCardName>{composition.label}</CompositionCardName>
    </CompositionCardWrapper>
  );
}

const CompositionSettingsContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  scrollbar-gutter: stable both-edges;
  padding-left: var(--spacing-sm);
  padding-right: var(--spacing-sm);
  /* No bottom spacing here: the panel card's Body already contributes
     --spacing-md, which is the whole of what the Speakers panel uses and is
     the pattern every tab now follows. Adding more here double-counted it. */
  box-sizing: border-box;
`;

const AllCompositionsForSubcategoryWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-top: 10px;
`;

const SubcategoryHeader = styled.div`
  display: flex;
  height: 24px;
  justify-content: space-between;
  align-items: center;
`;

const SubcategoryChips = styled(ChipGroup)``;

const SubcategoryChip = styled(Chip)``;

// Grid for "See All" view
const CompositionGridWrapper = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
  margin-bottom: 16px;
  overflow: hidden;
`;

const AddCompositionButton = styled.div<{
  isQuoteCard?: boolean;
}>`
  width: 100%;
  min-width: 0;
  height: auto;
  aspect-ratio: ${props => (props.isQuoteCard ? '1 / 1' : '145 / 79')};
  border-radius: var(--radius-input);
  overflow: hidden;
  cursor: pointer;
  transition: var(--transition-normal);
  background: var(--surface);
  display: block;
  position: relative;
`;

const ThumbnailImage = styled.img<{ isQuoteCard?: boolean }>`
  width: 100%;
  height: auto;
  aspect-ratio: ${props => (props.isQuoteCard ? '1 / 1' : '16 / 9')};
  display: block;
  object-fit: cover;
  background-color: var(--background);
`;

const PlaceholderContent = styled.div<{ isQuoteCard?: boolean }>`
  width: 100%;
  height: auto;
  aspect-ratio: ${props => (props.isQuoteCard ? '1 / 1' : '16 / 9')};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px;
  text-align: center;
  font-size: var(--text-xs);
  color: var(--text-primary);
  background-color: var(--background);
`;

const CompositionCardWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 4px;
  position: relative;
  min-width: 0;
  overflow: visible;
`;

/* Matches the Upload tile's busy state beside it: the shared Spinner over an
   xs medium label, on the tile's own background. */
const PendingThumbnail = styled(PlaceholderContent)`
  flex-direction: column;
  gap: 4px;
  color: var(--text-secondary);
`;

const PendingThumbnailText = styled.span`
  font-family: var(--font-primary);
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
`;

const CompositionCardName = styled.p`
  color: var(--text-secondary);
  font-family: var(--font-primary);
  font-size: var(--text-xs);
  font-style: normal;
  font-weight: var(--font-weight-normal);
  line-height: 159%;
  max-width: 145px;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
`;

const DeleteButton = styled.button`
  position: absolute;
  top: -4px;
  right: -4px;
  background: var(--surface-elevated);
  border: none;
  border-radius: 4px;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  color: var(--text-muted);
  opacity: 0;
  transition:
    opacity 0.15s ease,
    background 0.15s ease,
    color 0.15s ease;

  ${CompositionCardWrapper}:hover & {
    opacity: 1;
  }

  &:hover {
    background: var(--border);
    color: var(--foreground);
  }
`;

const CardBorder = styled.div<{ isActive: boolean; isHovered: boolean }>`
  border-radius: 5px;
  border: 2px solid
    ${props =>
      props.isActive || props.isHovered ? 'var(--primary)' : 'transparent'};
  transition: border-color 0.15s ease;
  overflow: hidden;
  cursor: pointer;
  display: block;
  min-width: 0;
  width: 100%;
  box-sizing: border-box;
`;

const PresetDimOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.56);
  border-radius: var(--radius-input);
  z-index: 2;
  pointer-events: none;
`;

const GlassButtonWrapper = styled.div`
  position: absolute;
  display: flex;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 3;
`;

const GlassButton = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 5px 10px;
  border-radius: 4px;
  background-color: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: white;
  font-family: var(--font-primary);
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  cursor: pointer;
  transition: all var(--transition-normal) ease;

  &:hover {
    background-color: rgba(255, 255, 255, 0.25);
    border-color: rgba(255, 255, 255, 0.5);
  }
`;
